import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useContractorCardContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module } from "@/constants";
import {
  ContractorCardFileUploadActions,
  ContractorCardFileDropZone,
  ContractorCardFilePreview,
} from "@/modules/contractor-panel/cards/shared/components";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { useCardFileUpload } from "@/modules/contractor-panel/cards/shared/hooks/useCardFileUpload";
import { ContractorCardsService } from "@/services/contractorCards";

export const ContractorCardUpload = () => {
  const { t } = useTranslation();
  const { data, loadingState } = useContractorCardContext();
  const { can_update } = useModulePermissions(Module.ContractorCards);

  const {
    pendingFile,
    isUploading,
    isDragging,
    dragHandlers,
    handleFileChange,
    handleViewFile,
    handleConfirmSend,
    clearPendingFile,
    handleFileInputClick,
    previewDialog,
    handleClosePreview,
  } = useCardFileUpload({
    cardId: data?.id,
    cardS3Key: data?.card_s3_key,
    queryKey: Module.ContractorCards,
    patchCard: (cardId, data) => ContractorCardsService.patch(cardId, data),
  });

  const isLoading = loadingState.isLoading || isUploading;
  const isUploadDirty = !!pendingFile;

  const displayFile = useMemo(
    () =>
      pendingFile ||
      (data?.card_filename
        ? {
            filename: data.card_filename,
            mimetype: data.card_mimetype || "",
          }
        : null),
    [pendingFile, data?.card_filename, data?.card_mimetype],
  );

  if (!data) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">
        {t("contractorCards.fields.card")}
      </h3>

      {displayFile ? (
        <ContractorCardFilePreview
          filename={displayFile.filename}
          mimetype={displayFile.mimetype}
          isPending={isUploadDirty}
          isDragging={isDragging}
          dragHandlers={dragHandlers}
          s3Key={pendingFile ? undefined : data?.card_s3_key}
          pendingFileContent={pendingFile?.content}
        />
      ) : (
        <ContractorCardFileDropZone
          isDragging={isDragging}
          dragHandlers={dragHandlers}
          onClick={handleFileInputClick}
        />
      )}

      <ContractorCardFileUploadActions
        hasFile={!!displayFile}
        hasPendingFile={isUploadDirty}
        isUploading={isLoading}
        canUpdate={can_update}
        onFileChange={handleFileChange}
        onConfirmSend={handleConfirmSend}
        onViewFile={handleViewFile}
        onCancelPending={clearPendingFile}
      />

      {previewDialog && (
        <FilePreviewDialog
          open
          onClose={handleClosePreview}
          fileUrl={previewDialog.fileUrl}
          fileName={previewDialog.fileName}
          mimeType={
            pendingFile?.mimetype ||
            (data?.card_mimetype ? data.card_mimetype : undefined)
          }
        />
      )}
    </div>
  );
};
